import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { navigate } from 'gatsby'
import { useFeatureIsOn } from '@growthbook/growthbook-react'

import {
  StyledBottomBar,
  StyledBottomNavButton,
  StyledBottomNavButtonIcon,
  StyledBottomNavButtonLabel,
} from './styles'

import ExitIcon from '../../../images/cashback/icon_exit.svg'
import DepositIcon from '../../../images/icon_plus_green.svg'
import CashbackIcon from '../../../images/icon_cashback_white.svg'
import { Drawer } from '../../atoms/drawer'
import { FormatAmount } from '../../atoms/formatAmount'
import { AuthContext } from '../../../context/authProvider'
import { useTranslation } from '../../../context/translationProvider'
import { If } from '../../atoms/if'
import { DepositDrawerContent } from '../../molecules/InGameDepositContent'
import { QuitGameConfirmation } from '../../molecules/gameQuitConfirmation'
import { useDepositApiData } from '../../../hooks/graphqlStaticQuery/useDepositApiData'
import { sendMobileAppEvent } from '../../../utils/mobileAppFunctionality'
import { getFullHistory } from '../../../utils/historyFunctions'
import {
  CASINO_PROVIDERS,
  IN_GAME_DEPOSIT_OPEN_DEPOSIT_DRAWER_EVENT,
  IN_GAME_DRAWER_TRANSITION_DURATION,
  INGAME_DRAWERS,
} from '../../../utils/constants'
import storageService from '../../../services/storageService'
import { useDepositData } from '../../../hooks/useDepositData'
import useSlugs from '../../../hooks/useSlugs'
import { InGameCashbackSection } from '../InGameCashbackSection'
import { useEventTracking } from '../../../context/eventTrackingProvider'
import { DISMISSAL_METHODS } from '../../../utils/constants'
import { InGameTour } from '../InGameTour'

const MANDATORY_GAP_SIZE = 50
const BOTTOM_NAVIGATION_SIZE = 56
const DRAWER_CUSTOM_MARGIN = MANDATORY_GAP_SIZE + BOTTOM_NAVIGATION_SIZE
const CUSTOM_SWIPE_DISTANCE = 250

const NAV_ACTIONS = {
  DEPOSIT: 'deposit',
  ADD_BANK_ACCOUNT: 'add_bank_account',
  EXIT: 'exit',
  CASHBACK: 'cashback',
}

const InGameBottomNavigation = ({
  gameType,
  game_sub_type
}) => {
  const { translate } = useTranslation()
  const {
    user,
    vaultAmount,
    shorterDeadline,
    cashout,
    loadingCashout,
    activeCards,
    hasMoreClaimedCards,
    getClaimedData,
    claimedCards,
  } = useContext(AuthContext)

  const shouldHideAmount = useFeatureIsOn('fe_igp_hide_amount_in_game_deposit')
  const { trackEvent, EVENT_NAMES, EVENT_LOCATIONS, getTrackingContext } = useEventTracking()

  const [
    casinoSlug,
    liveCasinoSlug,
    crashGamesSlug,
    loginSlug,
    registerSlug,
    forgotPasswordSlug,
  ] = useSlugs([
    'casino',
    'live-casino',
    'crash-games',
    'login',
    'register',
    'forgot-password',
  ])

  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [drawerType, setDrawerType] = useState(null)
  const [isIncreasing, setIsIncreasing] = useState(false)
  const [disableTransition, setDisableTransition] = useState(false)
  const [isDepositSecondStep, setIsDepositSecondStep] = useState(false)
  const [isClosing, setIsClosing] = useState(false)

  const previousVaultAmountRef = useRef(vaultAmount)
  const timerRef = useRef(null)

  const depositLobbyApiData = useDepositApiData()
  const {
    depositLobbyData,
    usableBankAccounts,
    hasEligibleBankAccount,
  } = useDepositData()

  const isLeaveDrawer = drawerType === NAV_ACTIONS.EXIT
  const isCashbackDrawer = drawerType === NAV_ACTIONS.CASHBACK
  const isDepositDrawer = drawerType === NAV_ACTIONS.DEPOSIT

  const shouldAttemptAppDepositFlow = (type) => {
    const isDepositType = type === NAV_ACTIONS.DEPOSIT
    return isDepositType && hasEligibleBankAccount
  }

  const handleNavClick = (type) => {
    if (isClosing) return

    const isSameDrawer = drawerType === type
    const navItemType = type === NAV_ACTIONS.DEPOSIT && !hasEligibleBankAccount 
    ? NAV_ACTIONS.ADD_BANK_ACCOUNT 
    : type

    if (isDrawerOpen && isSameDrawer) {
      handleCloseDrawer({ dismissal_method: DISMISSAL_METHODS.NAV_REPEAT_TAP })
      return
    }
    
    trackEvent(EVENT_NAMES.INGAME_NAVBAR_TAPPED, {
      nav_item: navItemType,
      game_type: gameType,
      ...getTrackingContext(),
    }, true)

    const triggerAppDepositFlow =
      shouldAttemptAppDepositFlow(type) &&
      sendMobileAppEvent('depositFromHeader')

    if (triggerAppDepositFlow) return

    const isExitDrawer = type === NAV_ACTIONS.EXIT
    const shouldSkipQuitGameConfirmation =
      storageService.getValue('gameCloseConfirmationDoNotShow') === 'true'
    const shouldAutoExit = isExitDrawer && shouldSkipQuitGameConfirmation

    if (shouldAutoExit) {
      handleLeaveAction()
      return
    }

    if (isDrawerOpen && !isSameDrawer) {
      setDisableTransition(true)
      setIsDrawerOpen(false)

      setTimeout(() => {
        setDrawerType(type)
        setDisableTransition(false)
        setIsDrawerOpen(true)
      }, 20)

      return
    }

    setDrawerType(type)
    setIsDrawerOpen(true)
  }

  const handleCloseDrawer = ({ type, dismissal_method: providedMethod } = {}) => {
    let dismissal_method = DISMISSAL_METHODS.DRAG_DOWN
    
    if (type === 'click') {
      dismissal_method = DISMISSAL_METHODS.TAP_OUTSIDE
    } else if (providedMethod) {
      dismissal_method = providedMethod
    }
    
    trackEvent(EVENT_NAMES.INGAME_DRAWER_DISMISSED, {
      drawer: drawerType,
      dismissal_method,
      ...getTrackingContext(),
    }, true)
    
    setIsDrawerOpen(false)

    if (isDepositSecondStep) {
      setIsDepositSecondStep(false)
    }
    setIsClosing(true)
    
    setTimeout(() => {
      setDrawerType(null)
      setIsClosing(false)
    }, IN_GAME_DRAWER_TRANSITION_DURATION)

  }

  const handleSetInGameBackUrl = () => {
    storageService.setValue('inGameBackUrl', window.location.pathname)
  }

  const getLeaveDestination = (game_sub_type, gameType = 'casino') => {
    const isLiveCasinoGameType = gameType === 'live_casino'
    const isCrashGamesGameSubtype = game_sub_type === 'crash_games'

    if (isLiveCasinoGameType) return `/${liveCasinoSlug}`
    if (isCrashGamesGameSubtype) return `/${crashGamesSlug}`

    return `/${casinoSlug}`
  }

  const isAuthRoute = (path = '') => {
    return (
      path.includes(loginSlug) ||
      path.includes(registerSlug) ||
      path.includes(forgotPasswordSlug)
    )
  }

  const handleLeaveAction = () => {
    const history = getFullHistory() ?? []
    const currentRoute = history[history.length - 1]
    const previousRoute = history[history.length - 2]
    const leaveDestination = getLeaveDestination(game_sub_type, gameType)

    const hasBackEntry = history.length >= 2
    const previousIsAuth = isAuthRoute(previousRoute)
    const previousIsSameAsCurrent = previousRoute === currentRoute
    const previousIsRoot = previousRoute === '/'

    const shouldGoToLeaveDestination =
      !hasBackEntry || previousIsAuth || previousIsSameAsCurrent

    const shouldGoToRoot =
      hasBackEntry && previousIsRoot && !shouldGoToLeaveDestination

    if (shouldGoToLeaveDestination) {
      navigate(leaveDestination)
      return
    }

    if (shouldGoToRoot) {
      navigate('/')
      return
    }

    navigate(-1)
  }

  useEffect(() => {
    const vaultIncreased = vaultAmount > previousVaultAmountRef.current
    previousVaultAmountRef.current = vaultAmount

    if (!vaultIncreased) return

    if (!isIncreasing) {
      setIsIncreasing(true)
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }

    timerRef.current = setTimeout(() => {
      setIsIncreasing(false)
      timerRef.current = null
    }, 1000)

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [vaultAmount])

  useEffect(() => {
    if (isDrawerOpen && isLeaveDrawer) {
      trackEvent(EVENT_NAMES.INGAME_DRAWER_OPENED, {
        drawer: INGAME_DRAWERS.EXIT,
        site_location: EVENT_LOCATIONS.INGAME_NAVBAR,
        ...getTrackingContext(),
      }, true)
    }
    // important: this should only be called once when drawer is opened, hence partial dependency list
  }, [isDrawerOpen, isLeaveDrawer])

  const handleOpenDepositDrawerEvent = useCallback(() => {
    const triggerAppDepositFlow =
      isDrawerOpen &&
      shouldAttemptAppDepositFlow(drawerType) &&
      sendMobileAppEvent('depositFromHeader')

    if (triggerAppDepositFlow) return

    setDrawerType(NAV_ACTIONS.DEPOSIT)
    setIsDrawerOpen(true)
  }, [drawerType, hasEligibleBankAccount, isDrawerOpen])

  useEffect(() => {
    document.body.addEventListener(
      IN_GAME_DEPOSIT_OPEN_DEPOSIT_DRAWER_EVENT,
      handleOpenDepositDrawerEvent
    )
    return () =>
      document.body.removeEventListener(
        IN_GAME_DEPOSIT_OPEN_DEPOSIT_DRAWER_EVENT,
        handleOpenDepositDrawerEvent
      )
  }, [handleOpenDepositDrawerEvent])

  return (
    <>
      <If condition={user?.user_capabilities?.play_casino} render={() => <InGameTour/>}/>
      <StyledBottomBar>
        <StyledBottomNavButton
          id="ingame-exit-button"
          onClick={() => handleNavClick(NAV_ACTIONS.EXIT)}
        >
          <StyledBottomNavButtonIcon src={ExitIcon} />
          <StyledBottomNavButtonLabel>
            {translate('game.bottom-nav-tab.text-exit')}
          </StyledBottomNavButtonLabel>
        </StyledBottomNavButton>

        <StyledBottomNavButton
          id="ingame-deposit-button"
          onClick={() => handleNavClick(NAV_ACTIONS.DEPOSIT)}
        >
          <StyledBottomNavButtonIcon src={DepositIcon} />
          <StyledBottomNavButtonLabel>
            <If
              condition={shouldHideAmount}
              render={() => translate('navigation.deposit')}
              renderElse={() => (
                <FormatAmount
                  amount={user?.wallet?.amount}
                  shortCode={user?.wallet?.currency?.short_code}
                  currencySymbol={user?.wallet?.currency?.symbol}
                  minimumFractionDigits={2}
                  maximumFractionDigits={2}
                />
              )}
            />
          </StyledBottomNavButtonLabel>
        </StyledBottomNavButton>

        <StyledBottomNavButton
          id="ingame-cashback-button"
          onClick={() => handleNavClick(NAV_ACTIONS.CASHBACK)}
        >
          <StyledBottomNavButtonIcon src={CashbackIcon} />
          <StyledBottomNavButtonLabel
            amount={vaultAmount}
            isIncreasing={isIncreasing}
          >
            <FormatAmount
              amount={vaultAmount}
              shortCode={user?.wallet?.currency?.short_code}
              currencySymbol={user?.wallet?.currency?.symbol}
              minimumFractionDigits={2}
              maximumFractionDigits={2}
            />
          </StyledBottomNavButtonLabel>
        </StyledBottomNavButton>
      </StyledBottomBar>

      <Drawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        position="bottom"
        customMargin={DRAWER_CUSTOM_MARGIN}
        theme={NAV_ACTIONS.CASHBACK === drawerType ? 'black' : 'light'}
        disableTransition={disableTransition}
        customSwipeDistance={isDepositSecondStep ? CUSTOM_SWIPE_DISTANCE : null}
      >
        <If
          condition={isLeaveDrawer}
          render={() => (
            <QuitGameConfirmation
              handleCloseDrawer={handleCloseDrawer}
              handleLeaveAction={handleLeaveAction}
            />
          )}
        />
        <If
          condition={isCashbackDrawer}
          render={() => (
            <InGameCashbackSection
              vaultAmount={vaultAmount}
              activeCards={activeCards}
              claimedCards={claimedCards}
              shorterDeadline={shorterDeadline}
              user={user}
              loadingCashout={loadingCashout}
              cashout={cashout}
              getClaimedData={getClaimedData}
              hasMoreClaimedCards={hasMoreClaimedCards}
              isDrawerOpen={isDrawerOpen}
            />
          )}
        />
        <If
          condition={isDepositDrawer}
          render={() => (
            <DepositDrawerContent
              user={user}
              depositLobbyData={depositLobbyData}
              depositLobbyCMSData={depositLobbyApiData}
              usableBankAccounts={usableBankAccounts}
              hasEligibleBankAccount={hasEligibleBankAccount}
              onClose={handleCloseDrawer}
              onSetBackUrl={handleSetInGameBackUrl}
              customMargin={DRAWER_CUSTOM_MARGIN}
              setIsDepositSecondStep={setIsDepositSecondStep}
            />
          )}
        />
      </Drawer>
    </>
  )
}

export { InGameBottomNavigation }
